import { useMemo, useState } from 'react';
import { BUCKETS, type Bucket, type Command, type Perspective } from '@integrate/contracts';
import {
  countsInHeadcount,
  subtreePeople,
  currentBucket,
  fmt,
  fmtMoney,
  getActiveLevels,
  getHeadFor,
  getHeadSource,
  resolveField,
  targetUnitOptions,
  type ChartNode,
  type GroupData,
  type Person,
  type SeparationState,
} from '@integrate/core';
import { ICON, IconButton } from '../ui/Icon.js';
import { Section } from './sections.js';
import { removeHeadCommand } from './heads.js';

const PERSPECTIVE_LABEL: Record<Perspective, string> = {
  'as-is': 'As-is',
  remain: 'RemainCo',
  'carve-out': 'Carve-out',
};

/**
 * A group, ported from `openGroupPanel` (orgchart.html:5488).
 *
 * The rollups are read off the hierarchy the chart is already drawing rather than recomputed here, which
 * is the point of lifting `buildHierarchy` into the app: a group's headcount, FTE and cost are what the
 * card on the chart says, and a panel that worked them out independently would eventually disagree with
 * the box the user just clicked.
 *
 * Two things it can do, both scoped to what the current view means:
 *
 *   · Move everyone in the group into a bucket, as one batch carrying explicit (person, value) pairs —
 *     never a predicate. Which people are "in this group" depends on this tab's lens, filters and
 *     overrides, and the server does not share them.
 *   · Rename or flatten, on a scenario perspective only. Renaming on as-is would be changing the source
 *     data for everyone, which is a data correction rather than a design decision.
 *
 * Under the target view it is a different panel entirely: the node is a declared unit rather than a
 * real group, so the restructure controls give way to the unit's own CRUD and a bulk placement.
 *
 * Not here yet, and said so on screen: bulk field edits, adding a build-up, moving a real group to a
 * different parent, and the group's slice of the change log.
 */
export function GroupPanel({
  state,
  node,
  nodes,
  perspective,
  targetView,
  pivot,
  decisions,
  readOnly,
  scenarioId,
  onDispatch,
  onSelectPerson,
}: {
  state: SeparationState;
  node: ChartNode & { kind: 'group' };
  nodes: readonly ChartNode[];
  perspective: Perspective;
  /**
   * Whether a custom pivot is active, passed rather than inferred.
   *
   * A pivot group is a bucket of people who share a field value, not a group the organisation has — so
   * every mutating affordance on this panel would write against a key nothing resolves. The panel
   * cannot work this out for itself: `data.levelKey` under a pivot is a perfectly ordinary field id.
   */
  pivot: boolean;
  /**
   * Whether the node came from the target blueprint, passed rather than inferred.
   *
   * Under the target view a group IS a declared unit: `data.levelKey` is `tlvlN` and `data.path` is the
   * unit's path. The restructure controls below would happily commit `renameGroup` and
   * `setLevelDisabled` with that key — both handlers accept it, so the write lands and appears in
   * everyone's activity feed — and nothing reads it, because every `tlvlN` lookup is routed to the
   * target overrides instead. The user would believe a unit had been renamed when it had not.
   */
  targetView: boolean;
  decisions: Readonly<Record<string, Bucket>>;
  readOnly: boolean;
  scenarioId: string;
  /** One command, or several as one atomic change — the structured placement needs three. */
  onDispatch(command: Command | readonly Command[]): void;
  /**
   * Open somebody's own panel. The head's name is the only thing on this panel that names a PERSON.
   *
   * A callback rather than a store reach, like every other panel here: `grep -n useStore panels`
   * returns nothing, and this is not the file to start.
   */
  onSelectPerson(employeeId: string): void;
}): JSX.Element {
  const data: GroupData = node.data;
  const [renameTo, setRenameTo] = useState(data.name);

  /*
    Everyone under this group, taken from the rendered tree.

    Descendants rather than direct children: a division's panel reports on the whole division, and the
    person nodes are already positioned under the right group by the same lens the chart used.

    HEADS ARE NOT LEAVES, and in the reporting layout PEOPLE HAVE CHILDREN. Both rules — and the reasons
    for them — now live in `subtreePeople`, because this walk was written twice: here, and again in the
    deck's `descendantPeople`. Multi-select needs the same answer, and "everyone under this group" giving
    two answers depending on which control the user reached for is the defect that made it worth one
    function. The shipped app says "the head included" on the button that opens this panel.
  */
  const members: Person[] = useMemo(() => subtreePeople(nodes, node.id), [nodes, node.id]);

  const counted = members.filter(countsInHeadcount);
  const tally: Record<Bucket, number> = { Remain: 0, 'Carve-out': 0, 'No longer required': 0 };
  for (const person of counted) tally[decisions[person.id] ?? 'Remain'] += 1;

  const buildUps = members.filter((m) => m.isBuildUp).length;
  const open = members.filter((m) => m.openPosition).length;
  const inactive = members.filter((m) => m.inactive).length;

  const headId = getHeadFor(state, node.id, perspective, (id) => currentBucket(state.design, id));
  /*
    Build-ups included: a planned hire can head a group — `deleteBuildUp` clears their designations for
    exactly that reason (packages/core/src/commands/registers.ts) — and looking only in the roster made
    the panel report "no head" for a group whose card on the chart was showing one.
  */
  const head = headId
    ? state.employees.find((e) => e.id === headId) ??
      state.design.buildUps.find((b) => b.id === headId) ??
      null
    : null;
  const headSource = getHeadSource(state, node.id, perspective, (id) => currentBucket(state.design, id));

  const bulkTo = (bucket: Bucket): void => {
    // Explicit pairs, so the command does not depend on the server resolving "this group" the same way
    // this tab did.
    const assignments = members
      .filter((m) => !(m.isBuildUp && bucket === 'No longer required'))
      .map((m) => ({ employeeId: m.id, bucket }));
    if (!assignments.length) return;
    /*
      No `src`. Reassigning a team is not a statement about who decided that one of its members was
      already a rule pick, and the shipped app agrees — its group bulk calls setBucket with no metadata
      (`orgchart.html:5612`), so an existing carve-out member keeps the source they had. Stamping
      'manual' here would quietly remove every rule pick in the group from the perimeter slider's reach,
      and the only symptom would be that dragging the slider later did nothing to them.
    */
    onDispatch({ type: 'setBucketBulk', scenarioId, assignments });
  };

  const canRestructure = perspective !== 'as-is';

  return (
    <>
      <Section
        testId="group-panel"
        title={
          <>
            <span data-testid="group-name">{data.name}</span>
            <span className="pill">{data.levelLabel}</span>
          </>
        }
      >
        {data.path.map((p) => (
          <div className="sp-row" key={p.levelKey}>
            <span className="k">{p.label}</span>
            <span className="v">{p.value}</span>
          </div>
        ))}
        <div className="sp-row">
          <span className="k">Headcount</span>
          <span className="v" data-testid="group-headcount">
            {fmt(data.count)}
          </span>
        </div>
        <div className="sp-row">
          <span className="k">Total FTE</span>
          <span className="v">{fmt(data.fte, 1)}</span>
        </div>
        <div className="sp-row">
          <span className="k">Total salary</span>
          <span className="v">{fmtMoney(data.salary)}</span>
        </div>
        {/*
          No head in the blueprint. `buildHierarchy` short-circuits head resolution in three separate
          places under the target view, so `data.head` is always null here — and offering a designation
          would write a `scenario_heads` row at perspective 'target' that nothing ever resolves.
        */}
        {!targetView && (
        <div className="sp-row">
          <span className="k">Head</span>
          <span className="v" data-testid="group-head">
            {head ? (
              <>
                {/*
                  THE NAME IS A WAY TO THE PERSON, which it was not before.

                  This panel was the only place in the product that told you who heads a group, and it
                  told you as dead text: to do anything about them — including stop them being the head,
                  which is only offered on their own panel — you had to already know who they were and
                  find their card on the chart. The same `linkish` + `onSelectPerson` pair the selection
                  panel's member list uses, so the gesture is the one people already know.
                */}
                <button
                  className="linkish"
                  data-testid="group-head-open"
                  title={`Open ${head.name}`}
                  onClick={() => onSelectPerson(String(head.id))}
                >
                  {head.name}
                </button>
                {headSource === 'inherited' && <span className="pill">inherited</span>}
                {/*
                  And a way to take the designation back from HERE. Removing a head is a decision about
                  the GROUP — "this team has nobody leading it in the carve-out" — and it was reachable
                  only from the person, which is the wrong object to be looking at while you make it.

                  Which command that is depends on whether inheritance would put the same person
                  straight back; `removeHeadCommand` is the one place that decides, shared with the
                  person panel's own star toggle.
                */}
                {!readOnly && (
                  <IconButton
                    icon={ICON.remove}
                    testId="group-head-remove"
                    label={`Remove ${head.name} as head of this group`}
                    onClick={() =>
                      onDispatch(
                        removeHeadCommand(state, {
                          scenarioId,
                          perspective,
                          groupId: node.id,
                          headId: String(head.id),
                        }),
                      )
                    }
                  />
                )}
              </>
            ) : (
              /* Designating one is still the person's own panel: it needs somebody chosen first. */
              '— not designated'
            )}
          </span>
        </div>
        )}
      </Section>

      <Section
        testId="group-breakdown"
        title="Bucket breakdown"
      >
        {BUCKETS.map((b) => (
          <div className="sp-row" key={b}>
            <span className="k">{b}</span>
            <span className="v" data-testid={`group-count-${b.replace(/\s+/g, '-')}`}>
              {fmt(tally[b])}
            </span>
          </div>
        ))}
        {buildUps > 0 && (
          <div className="sp-row">
            <span className="k">Build-ups</span>
            <span className="v">{fmt(buildUps)}</span>
          </div>
        )}
        {open > 0 && (
          <div className="sp-row">
            <span className="k">Open positions</span>
            <span className="v">{fmt(open)}</span>
          </div>
        )}
        {inactive > 0 && (
          <div className="sp-row">
            <span className="k">Inactive</span>
            <span className="v">{fmt(inactive)}</span>
          </div>
        )}
      </Section>

      <Section
        testId="group-bulk"
        title="Bulk bucket assignment"
      >
        <p className="muted">
          Apply to all {fmt(members.length)} people under this {data.levelLabel.toLowerCase()}.
          Build-ups skip &ldquo;No longer required&rdquo;.
        </p>
        <div className="toggle-row">
          {BUCKETS.map((b) => (
            <button
              key={b}
              className="toggle-btn"
              data-testid={`group-bulk-${b.replace(/\s+/g, '-')}`}
              disabled={readOnly || members.length === 0}
              onClick={() => bulkTo(b)}
            >
              → {b}
            </button>
          ))}
        </div>
      </Section>

      {/*
        Placing a whole real team into the blueprint.

        On a REAL group, not on a target unit: the gesture mirrors this team's own shape into the
        design (`applyTargetTeamStructured`, `:4639`), so it needs the real hierarchy under the cursor.
        Under the target view there is no real team to mirror.
      */}
      {!targetView && !pivot && state.targetEnabled && (
        <TargetPlacementForGroup
          state={state}
          data={data}
          members={members}
          perspective={perspective}
          decisions={decisions}
          readOnly={readOnly}
          scenarioId={scenarioId}
          onDispatch={onDispatch}
        />
      )}

      {/*
        Under a PIVOT there is nothing here to restructure.

        A pivot group is a bucket of people who share a field value, not a real group: its `levelKey` is
        the pivot's field id, so a rename would write a scenario `groupRenames` row on (say) `country`
        that every filter facet, every card and the severance grid keys then read — a data change the
        user believed was a relabelled box — and FLATTEN would disable a level the hierarchy does not
        have. The app disables the whole section for the same reason and says so (`orgchart.html:5522`).
      */}
      {pivot ? (
        <Section
          testId="group-pivot-note"
          title="Restructure"
        >
          <p className="muted">
            Custom grouping active (view only) — restructuring is unavailable, because this box is a
            bucket of people who share a value rather than a group the organisation has. Clear the pivot
            to change the structure.
          </p>
        </Section>
      ) : targetView ? (
        <TargetUnitControls state={state} data={data} readOnly={readOnly} onDispatch={onDispatch} />
      ) : (
      <Section
        testId="group-restructure"
        title={<>Restructure{canRestructure ? ` in ${PERSPECTIVE_LABEL[perspective]}` : ''}</>}
      >
        {canRestructure ? (
          <>
            <p className="muted">
              Changes apply only to the {PERSPECTIVE_LABEL[perspective]} view; as-is is unchanged.
              Renaming to an existing group&rsquo;s name merges them.
            </p>
            <div className="field">
              <label htmlFor="group-rename">Rename &ldquo;{data.name}&rdquo; to</label>
              <div className="field-row">
                <input
                  id="group-rename"
                  data-testid="group-rename"
                  value={renameTo}
                  disabled={readOnly}
                  onChange={(e) => setRenameTo(e.target.value)}
                />
                <button
                  className="primary"
                  data-testid="group-rename-apply"
                  disabled={readOnly || !renameTo.trim() || renameTo.trim() === data.name}
                  onClick={() =>
                    onDispatch({
                      type: 'renameGroup',
                      scenarioId,
                      perspective,
                      levelKey: data.levelKey,
                      // The ORIGINAL as-is value, not the displayed one: renaming twice must replace the
                      // mapping rather than chain through it.
                      sourceValue: sourceValueOf(state, perspective, data),
                      newValue: renameTo.trim(),
                    })
                  }
                >
                  Apply
                </button>
              </div>
            </div>
            <button
              data-testid="group-flatten"
              style={{ width: '100%' }}
              disabled={readOnly}
              onClick={() =>
                onDispatch({
                  type: 'setLevelDisabled',
                  scenarioId,
                  perspective,
                  levelKey: data.levelKey,
                  disabled: !isFlattened(state, perspective, data.levelKey),
                })
              }
            >
              {isFlattened(state, perspective, data.levelKey)
                ? `↺ Restore the ${data.levelLabel} level`
                : `⇕ Flatten the ${data.levelLabel} level (skip it entirely)`}
            </button>
          </>
        ) : (
          <p className="muted">
            Renaming and flattening are scenario design decisions, so they are done from RemainCo or
            Carve-out. On as-is they would change the source data for every scenario at once.
          </p>
        )}
        {/*
          Narrowed rather than deleted. A bulk field edit IS on the platform now — from the SELECTION
          panel, not from here — so the sentence has to say which of the three it still means, and point at
          the capability that exists. Silently dropping the promise would be the same defect in reverse.
        */}
        <p className="muted">
          Bulk field edits from this panel, build-ups and moving this group to another parent are not on
          the platform yet — Ctrl/Cmd-click this group on the chart to take its team, and edit a field for
          all of them from there. Designate a head from a person&rsquo;s panel; place people into the target
          org from the Target view.
        </p>
      </Section>
      )}
    </>
  );
}

/**
 * The as-is value behind a possibly-renamed group.
 *
 * `renameGroup` keys on the original, and this panel is showing whatever the current perspective renamed
 * it to. Renaming "Ops" to "Operations" and then to "Operations & Field" has to replace the mapping from
 * "Ops", not add one from "Operations" — otherwise the rename chain grows a link every time and undoing
 * the second one leaves the first unreachable.
 *
 * The reverse lookup is the one the single-file app does before writing (`:5570`). No index for it,
 * because a rename map holds one entry per renamed group, not per person.
 */
function sourceValueOf(state: SeparationState, perspective: Perspective, data: GroupData): string {
  const renames = state.design.scenarioOverrides[perspective]?.groupRenames?.[data.levelKey] ?? {};
  for (const [source, renamed] of Object.entries(renames)) {
    if (renamed === data.name) return source;
  }
  return data.name;
}

function isFlattened(state: SeparationState, perspective: Perspective, levelKey: string): boolean {
  return (state.design.scenarioOverrides[perspective]?.disabledLevels ?? []).includes(levelKey);
}

/* ------------------------------------------------------------------ *
 * A declared target unit
 * ------------------------------------------------------------------ */

/**
 * The unit CRUD, ported from the canvas card's handlers (orgchart.html:4506–4553).
 *
 * Under the target view a group node IS a declared unit, and its identity is its path — there are no
 * ids. So every control here sends a path, and the one that moves a unit sends the destination's path
 * rather than a parent reference: the tree is content-addressed the same way the as-is hierarchy is.
 *
 * The app offers a pointer drag for the move as well (`:10565`); it routes to the same command, and a
 * select expresses it without a second interaction layer, so the drag is deliberately not ported.
 */
function TargetUnitControls({
  state,
  data,
  readOnly,
  onDispatch,
}: {
  state: SeparationState;
  data: GroupData;
  readOnly: boolean;
  onDispatch(command: Command | readonly Command[]): void;
}): JSX.Element {
  const path = useMemo(() => data.path.map((p) => p.value), [data.path]);
  const [child, setChild] = useState('');
  const [renameTo, setRenameTo] = useState(data.name);
  const [confirmRemove, setConfirmRemove] = useState(false);

  const options = useMemo(() => targetUnitOptions(state), [state]);
  /*
    Everywhere this unit could go, minus its own branch.

    Moving a unit inside itself is refused by the handler, but offering it and then refusing is worse
    than not offering it — and the top level is a destination too, which an empty path expresses.
  */
  const destinations = useMemo(
    () =>
      options.filter((o) => {
        const candidate = JSON.parse(o.value) as string[];
        return !path.every((segment, i) => candidate[i] === segment);
      }),
    [options, path],
  );

  const addChild = (): void => {
    const name = child.trim();
    if (!name) return;
    setChild('');
    onDispatch({ type: 'addTargetUnit', path: [...path, name] });
  };

  return (
    <>
      <Section
        testId="group-target-unit"
        title="Target unit"
      >
        <p className="muted">
          A box in the organisation being designed. Its identity is its path, so renaming or moving it
          rewrites every placement underneath in every scenario.
        </p>

        {!readOnly && (
          <div className="field">
            <label htmlFor="group-target-add-child">Add a sub-unit</label>
            <div className="field-row">
              <input
                id="group-target-add-child"
                data-testid="group-target-add-child"
                value={child}
                placeholder="e.g. Sales"
                onChange={(e) => setChild(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') addChild();
                }}
              />
              <button data-testid="group-target-add-child-apply" disabled={!child.trim()} onClick={addChild}>
                Add
              </button>
            </div>
          </div>
        )}

        {!readOnly && (
          <div className="field">
            <label htmlFor="group-target-rename">Rename to</label>
            <div className="field-row">
              <input
                id="group-target-rename"
                data-testid="group-target-rename"
                value={renameTo}
                onChange={(e) => setRenameTo(e.target.value)}
              />
              <button
                className="primary"
                data-testid="group-target-rename-apply"
                disabled={!renameTo.trim() || renameTo.trim() === data.name}
                onClick={() =>
                  onDispatch({
                    type: 'renameTargetValue',
                    levelIndex: data.levelIdx,
                    oldValue: data.name,
                    newValue: renameTo.trim(),
                  })
                }
              >
                Rename
              </button>
            </div>
            {/*
              A rename is BY VALUE and level-wide, which is the app's own rule (`:4446`) and not an
              approximation of it: two unrelated units called "Ops" at this tier are renamed together,
              and landing on an existing sibling merges the two — which cannot be undone, because the
              merge loses which people were in which unit.
            */}
            <p className="muted">
              Renames every unit called &ldquo;{data.name}&rdquo; at the {data.levelLabel} tier. Renaming
              onto an existing sibling merges them, and a merge cannot be undone.
            </p>
          </div>
        )}

        {!readOnly && (
          <div className="field">
            <label htmlFor="group-target-parent">Move under</label>
            <select
              id="group-target-parent"
              data-testid="group-target-parent"
              value=""
              onChange={(e) => {
                const raw = e.target.value;
                if (raw === '') return;
                onDispatch({
                  type: 'reparentTargetUnit',
                  path,
                  toParent: raw === '__top__' ? [] : (JSON.parse(raw) as string[]),
                });
              }}
            >
              <option value="">— choose a new parent —</option>
              <option value="__top__">(top level)</option>
              {destinations.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
        )}

        {!readOnly && (
          <>
            <button
              className="danger"
              style={{ width: '100%' }}
              data-testid="group-target-remove"
              onClick={() => setConfirmRemove(true)}
            >
              Remove this unit
            </button>
            {confirmRemove && (
              <div className="sp-row" data-testid="group-target-remove-confirm-row">
                <span className="k">
                  {/*
                    A top-level unit has no parent to move anyone up to, so those people leave the
                    target org entirely. The command's summary now says which of the two happened; the
                    confirmation has to say the same thing BEFORE it happens.
                  */}
                  Removes this unit and everything under it.{' '}
                  {path.length > 1
                    ? `Anyone placed inside moves up to “${path.slice(0, -1).join(' › ')}”.`
                    : 'Anyone placed inside leaves the target org entirely.'}{' '}
                  This cannot be undone.
                </span>
                <span className="v">
                  <button onClick={() => setConfirmRemove(false)}>Cancel</button>{' '}
                  <button
                    className="danger"
                    data-testid="group-target-remove-confirm"
                    onClick={() => {
                      setConfirmRemove(false);
                      onDispatch({ type: 'removeTargetUnit', path });
                    }}
                  >
                    Remove it
                  </button>
                </span>
              </div>
            )}
          </>
        )}
      </Section>

    </>
  );
}

/**
 * Bulk placement for a real team, flat or keeping its shape.
 *
 * Ported from the group panel's target block (orgchart.html:5587–5606). Flat drops everyone into the
 * chosen unit; STRUCTURED mirrors the team's own sub-levels underneath it, so a team of teams arrives
 * as a team of teams rather than as one flat box.
 */
function TargetPlacementForGroup({
  state,
  data,
  members,
  perspective,
  decisions,
  readOnly,
  scenarioId,
  onDispatch,
}: {
  state: SeparationState;
  data: GroupData;
  members: readonly Person[];
  /**
   * The perspective the real hierarchy was resolved through.
   *
   * Mirroring a team's shape reads each person's own level values, and those resolve through the
   * active perspective's overrides — so the shape depends on which side you are looking at.
   */
  perspective: Perspective;
  decisions: Readonly<Record<string, Bucket>>;
  readOnly: boolean;
  scenarioId: string;
  onDispatch(command: Command | readonly Command[]): void;
}): JSX.Element | null {
  const [placeTo, setPlaceTo] = useState('');
  const [mode, setMode] = useState<'flat' | 'structured'>('flat');
  const options = useMemo(() => targetUnitOptions(state), [state]);
  const carveMembers = members.filter((m) => (decisions[m.id] ?? 'Remain') === 'Carve-out');

  /*
    Placing a whole group, flat or keeping its shape.

    STRUCTURED mirrors the real hierarchy below this box into the blueprint (`:4639`): each person's
    path is the destination plus their own level values under this group, trimmed at the first blank —
    the same ragged rule the chart's grouping uses, so somebody with a Division and no Team lands in the
    Division rather than in a phantom empty one.

    Sent as ONE batch of three commands, in this order: grow the ladder to fit the deepest mirrored
    path, declare every unit that does not exist yet, then place. Each command in a batch sees the
    previous one's writes, so the placement lands in units the same transaction has just created — and
    the whole thing is one entry in the history and one undo step.

    `declareTargetUnits` rather than a client-side diff: which units already exist is the server's fact,
    and a diff computed here would lose the whole action to any concurrent declare.
  */
  const place = async (): Promise<void> => {
    if (!placeTo) return;
    const destination = placeTo === '__unplace__' ? null : (JSON.parse(placeTo) as string[]);
    setPlaceTo('');

    if (destination === null || mode === 'flat') {
      onDispatch({
        type: 'setTargetPlacement',
        scenarioId,
        assignments: carveMembers.map((m) => ({ employeeId: m.id, path: destination })),
      });
      return;
    }

    const levels = getActiveLevels(state, perspective);
    const suffixOf = (person: Person): string[] => {
      const out: string[] = [];
      for (let i = data.levelIdx + 1; i < levels.length; i++) {
        const value = resolveField(state, person, levels[i]!.key, perspective);
        if (value == null || String(value).trim() === '') break;
        out.push(String(value));
      }
      return out;
    };

    const assignments = carveMembers.map((m) => ({
      employeeId: m.id,
      path: [...destination, ...suffixOf(m)],
    }));
    const deepest = assignments.reduce((n, a) => Math.max(n, a.path.length), destination.length);
    const ladder = state.targetLevels ?? [];

    const commands: Command[] = [];
    if (deepest > ladder.length) {
      commands.push({
        type: 'setTargetLevels',
        levels: Array.from({ length: deepest }, (_, i) => ladder[i] ?? { key: `tlvl${i + 1}`, label: `L${i + 1}` }),
      });
    }
    commands.push({ type: 'declareTargetUnits', paths: assignments.map((a) => a.path) });
    commands.push({ type: 'setTargetPlacement', scenarioId, assignments });
    onDispatch(commands);
  };


  if (readOnly || !carveMembers.length) return null;

  return (
    <>
        <Section
          testId="group-target-place"
          title={
            <>
              Place this group <span className="pill">{fmt(carveMembers.length)} carve-out</span>
            </>
          }
        >
          <p className="muted">
            Puts every carve-out person currently under this box into the target org. Explicit pairs, so
            the server places exactly the people this tab is showing.
          </p>
          <div className="field-row">
            <select
              data-testid="group-target-place-select"
              value={placeTo}
              onChange={(e) => setPlaceTo(e.target.value)}
            >
              <option value="">— choose a unit —</option>
              <option value="__unplace__">(unplace them)</option>
              {options.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            <button
              className="primary"
              data-testid="group-target-place-apply"
              disabled={!placeTo}
              onClick={() => void place()}
            >
              Place
            </button>
          </div>
          {placeTo !== '__unplace__' && (
            <div className="toggle-row">
              <button
                className={`toggle-btn ${mode === 'flat' ? 'active' : ''}`}
                data-testid="group-target-mode-flat"
                aria-pressed={mode === 'flat'}
                title="Everyone lands in the chosen unit itself"
                onClick={() => setMode('flat')}
              >
                Flat
              </button>
              <button
                className={`toggle-btn ${mode === 'structured' ? 'active' : ''}`}
                data-testid="group-target-mode-structured"
                aria-pressed={mode === 'structured'}
                title="Recreate this team's sub-teams underneath the chosen unit"
                onClick={() => setMode('structured')}
              >
                Keep sub-teams
              </button>
            </div>
          )}
          {placeTo !== '__unplace__' && mode === 'structured' && (
            <p className="muted">
              Mirrors each person&rsquo;s real sub-levels below {data.levelLabel} into the blueprint, so
              a team of teams arrives as a team of teams. Units that do not exist yet are declared in the
              same change.
            </p>
          )}
        </Section>
    </>
  );
}
